import { useState } from 'react';
import { Type } from 'lucide-react';
import { cn } from '@lib/utils';

export interface TextSizeControlProps {
  label: string;
  size: number;
  // O retorno não é consumido; os setters do useFilaConfig devolvem o
  // resultado de forceUpdateConfig, e exigir `void` rejeitava esse formato.
  onSizeChange: (size: number) => void | boolean;
  // Opcionais: o componente já define padrão (1 e 20) na desestruturação.
  minLevel?: number;
  maxLevel?: number;
  className?: string;
  icon?: React.ReactNode;
}

// Função para converter nível para rem (nível 1 = 2rem)
const levelToRem = (level: number): number => {
  return 2 + (level - 1) * 0.25; // 2rem + (nível-1) * 0.25rem
};

// Função para converter rem para nível
const remToLevel = (rem: number): number => {
  return Math.round((rem - 2) / 0.25) + 1;
};

export function TextSizeControl({ label, size, onSizeChange, minLevel = 1, maxLevel = 20, className, icon }: TextSizeControlProps) {
  // Garantir que size nunca seja undefined e converter para nível
  const safeSize = size ?? 2;
  const currentLevel = Math.min(Math.max(remToLevel(safeSize), minLevel), maxLevel);

  const increaseLevel = () => {
    const newLevel = Math.min(currentLevel + 1, maxLevel);
    const newRem = levelToRem(newLevel);
    onSizeChange(newRem);
  };

  const decreaseLevel = () => {
    const newLevel = Math.max(currentLevel - 1, minLevel);
    const newRem = levelToRem(newLevel);
    onSizeChange(newRem);
  };

  const canIncrease = currentLevel < maxLevel;
  const canDecrease = currentLevel > minLevel;

  return (
    <div className={cn('flex items-center space-x-3', className)}>
      <div className="flex items-center space-x-2">
        {icon || <Type className="h-4 w-4 text-black" />}
        <span className="text-sm text-gray-600">{label}</span>
      </div>

      <div className="flex items-center space-x-2">
        <button
          onClick={decreaseLevel}
          disabled={!canDecrease}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full border shadow-sm transition-all duration-200',
            canDecrease
              ? 'border-gray-300 bg-white hover:scale-105 hover:bg-gray-800 hover:text-white hover:shadow-md'
              : 'cursor-not-allowed border-gray-200 bg-gray-50',
          )}
          title={`Diminuir para nível ${currentLevel - 1}`}>
          <span
            className={cn('text-lg font-semibold transition-colors duration-200', canDecrease ? 'text-gray-700 hover:text-white' : 'text-gray-400')}>
            −
          </span>
        </button>

        {/* <div className="flex min-w-[3rem] items-center justify-center">
          <span className="text-lg font-bold text-black">
            {currentLevel}
          </span>
        </div> */}

        <button
          onClick={increaseLevel}
          disabled={!canIncrease}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full border shadow-sm transition-all duration-200',
            canIncrease
              ? 'border-gray-300 bg-white hover:scale-105 hover:bg-gray-800 hover:text-white hover:shadow-md'
              : 'cursor-not-allowed border-gray-200 bg-gray-50',
          )}
          title={`Aumentar para nível ${currentLevel + 1}`}>
          <span
            className={cn('text-lg font-semibold transition-colors duration-200', canIncrease ? 'text-gray-700 hover:text-white' : 'text-gray-400')}>
            +
          </span>
        </button>
      </div>
    </div>
  );
}
